import { Headphones, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuthStore } from '@/stores/auth-store';
import { Badge, Button, Card, Page, PageContent } from '@bliks-io/ui';
import { NavBadge } from '@/layout/nav/NavMain';
import { ContactForm } from './components/ContactForm';
import { useSupportTickets } from '@/api/queries/useSupportTickets';
import { isTicketClosed } from './ticketStatus';

export function HelpCenterPage() {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: ticketData } = useSupportTickets();
  const tickets = ticketData?.tickets || [];
  const openCount = tickets.filter((tk) => !isTicketClosed(tk.status)).length;
  const newReplyCount = tickets.filter((tk) => tk.hasNewReply).length;
  useDocumentTitle('pageTitles.help');

  return (
    <Page>
      <PageContent>
        {/* Welcome Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-3">{t('helpCenter.title')}</h1>
          <p className="text-muted-foreground text-lg">{t('helpCenter.subtitle')}</p>
        </div>

        {/* Contact / Ticket Form */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-highlight-surface-strong rounded-lg">
                <Headphones className="w-5 h-5 text-highlight" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{t('helpCenter.contactUs')}</h2>
                <p className="text-sm text-muted-foreground">{t('helpCenter.contactSubtitle')}</p>
              </div>
            </div>
            <Link to="/$slug/help/tickets" params={{ slug }} className="relative">
              <Button variant="outline" size="small">
                <MessageSquare className="h-4 w-4 mr-1" />
                {t('helpCenter.viewTickets')}
                {openCount > 0 && (
                  <Badge
                    colorScheme="neutral"
                    className="ml-1.5"
                    aria-label={t('helpCenter.openTicketsCount', {
                      count: openCount
                    })}
                  >
                    {openCount}
                  </Badge>
                )}
              </Button>
              {newReplyCount > 0 && (
                <span
                  aria-label={t('helpCenter.ticketsWithNewReplyCount', {
                    count: newReplyCount
                  })}
                >
                  <NavBadge count={String(newReplyCount)} />
                </span>
              )}
            </Link>
          </div>

          <Card className="p-6">
            <ContactForm />
          </Card>
        </section>
      </PageContent>
    </Page>
  );
}
